import * as AnalyticsEventType from 'common/constants/analytics-event-type';
import * as VideoPlayerType from 'common/constants/video-player-type';
import CVP from 'cvp';
import CVPConfig from 'common/config/cvp';
import Environment from 'common/utils/environment';
import LoadingSpinner from 'common/components/loading-spinner';
import styles from './styles.less';
import VideoTracker from 'common/analytics/video-tracker';
import { action, observable, toJS, when } from 'mobx';
import { observer, propTypes } from 'mobx-react';
import { parseJSON } from 'common/utils/json-utils';
import { uniqueId } from 'lodash';


@observer
class CVPComponent extends React.Component {
    static propTypes = {
        onAdComplete: React.PropTypes.func,
        onAdStart: React.PropTypes.func,
        onTVERequestLogin: React.PropTypes.func,
        onVideoBuffer: React.PropTypes.func,
        onVideoEnded: React.PropTypes.func,
        onVideoComplete: React.PropTypes.func,
        onVideoFifty: React.PropTypes.func,
        onVideoFinished: React.PropTypes.func,
        onVideoPause: React.PropTypes.func,
        onVideoProgress: React.PropTypes.func,
        onVideoStart: React.PropTypes.func,
        tveAccessToken: React.PropTypes.object,
        video: propTypes.objectOrObservableObject
    };

    @observable hasTrackedFifty = false;
    @observable isBuffering = false;
    @observable isCVPReady = false;
    @observable token = null;

    constructor(props) {
        super(props);
        this.id = uniqueId('cvp');
        this.config = Object.assign({}, CVPConfig, {
            id: `${this.id}_player`,
            onCVPReady: this.handleCVPReady,
            onTrackingAdStart: this.handleTrackingAdStart,                 // video-preroll|midroll|postroll
            onTrackingAdComplete: this.handleTrackingAdComplete,           // video-preroll|midroll|postroll
            onContentBuffering: this.handleContentBuffering,               // video-buffer
            onContentComplete: this.handleContentComplete,
            onContentEntryLoad: this.handleContentEntryLoad,
            onTrackingContentComplete: this.handleTrackingContentComplete, // video-complete
            onTrackingContentPlay: this.handleTrackingContentPlay,         // video-start
            onTrackingContentProgress: this.handleTrackingContentProgress, // video-progress, video-fifty
            onTrackingPaused: this.handleTrackingPaused                    // video-pause
        });
    }

    componentWillMount() {
        this.cvpReadyListener = when(() => this.isCVPReady, () => this.setVideo());
    }

    componentDidMount() {
        if (this.props.video && this.props.video.auth && Environment.mobile) {
            return this.stop();
        }
        if (this.props.video) {
            this.getCVP();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.video && this.props.video.auth && Environment.mobile) {
            return this.stop();
        }
        if (this.props.video && !this.player) {
            this.getCVP();
        } else if (this.props.video !== prevProps.video) {
            this.setVideo();
        } else if (this.props.video && this.props.video.auth && this.props.tveAccessToken && !prevProps.tveAccessToken) {
            this.setVideo();
        }
    }

    componentWillUnmount() {
        this.cvpReadyListener();
        this.cvpReadyListener = null;
        if (this.player) {
            this.player.destroy();
        }
        this.player = null;
    }

    render() {
        const { video } = this.props;
        const style = {
            pointerEvents: video ? null : 'none',
            visibility: video ? null : 'hidden'
        };
        const showLoadingSpinner = (this.isBuffering || !this.isCVPReady || (video && video.auth && !this.props.tveAccessToken));
        return (
            <div
                className={ cx(styles.cvp, this.props.className) }
                style={ style }>
                { !!showLoadingSpinner &&
                <LoadingSpinner className={ styles.loadingSpinner } /> }
                <div id={ this.id }></div>
            </div>
        );
    }

    getCVP() {
        if (this.player) {
            return this.player;
        }
        // Causing CVP to not load correctly on simulcast page
        // if (this.props.video) {
            // this.config.flashVars.context = this.props.video.adContext;
        // }
        this.player = new CVP(this.config);
        this.player.embedSWF(this.id);
        return this.player;
    }

    pause() {
        if (this.player && !this.player.isPaused()) {
            this.player.pause();
        }
    }

    play() {
        if (this.player && this.player.isPaused()) {
            this.player.resume();
        }
    }

    setContext(context = this.config.flashVars.context) {
        if (!this.player || !context) {
            return;
        }
        this.player.switchAdContext(context);
    }

    setVideo() {
        if (!this.isCVPReady) {
            return;
        }
        if (!this.props.video) {
            return this.stop();
        }
        const { video } = this.props;
        const token = toJS(this.props.tveAccessToken);
        if (!video.auth && this.player) {
            this.setContext(video.adContext);
            this.player.play(video.id);
        } else if (video.auth && !this.props.tveAccessToken) {
            this.stop();
            this.props.onTVERequestLogin && this.props.onTVERequestLogin();
        } else if (this.player) {
            this.setContext(video.adContext);
            this.player.play(video.id, token);
        }
    }

    stop() {
        if (this.player) {
            this.player.stop();
        }
    }

    @action handleContentBuffering = (playerID, contentID, buffering) => {
        const eventData = Object.assign({ contentID, buffering }, this.contentEntry);
        this.isBuffering = buffering;
        VideoTracker.track(this.props.video, AnalyticsEventType.VIDEO_BUFFER, eventData, VideoPlayerType.CVP);
        this.props.onVideoBuffer && this.props.onVideoBuffer(this.props.video, eventData, VideoPlayerType.CVP);
    };

    @action handleCVPReady = () => {
        this.isCVPReady = true;
    };

    handleTrackingAdComplete = (playerID, dataObj) => {
        const eventData = Object.assign({}, dataObj, this.contentEntry);
        VideoTracker.track(this.props.video, AnalyticsEventType.AD_COMPLETE, eventData, VideoPlayerType.CVP);
        this.props.onAdComplete && this.props.onAdComplete(this.props.video, eventData, VideoPlayerType.CVP);
    };

    handleTrackingAdStart = (playerID, dataObj) => {
        const eventData = Object.assign({}, dataObj, this.contentEntry);
        VideoTracker.track(this.props.video, AnalyticsEventType.AD_START, eventData, VideoPlayerType.CVP);
        this.props.onAdStart && this.props.onAdStart(this.props.video, eventData, VideoPlayerType.CVP);
    };

    handleContentComplete = (playerID, contentID) => {
        const eventData = Object.assign({ contentID }, this.contentEntry);
        VideoTracker.track(this.props.video, AnalyticsEventType.VIDEO_ENDED, eventData, VideoPlayerType.CVP);
        this.props.onVideoEnded && this.props.onVideoEnded(this.props.video, eventData, VideoPlayerType.CVP);
        this.props.onVideoFinished && this.props.onVideoFinished(this.props.video, eventData, VideoPlayerType.CVP);
    };

    handleTrackingContentComplete = (playerID, dataObj) => {
        const eventData = Object.assign({}, dataObj, this.contentEntry);
        VideoTracker.track(this.props.video, AnalyticsEventType.VIDEO_COMPLETE, eventData, VideoPlayerType.CVP);
        this.props.onVideoComplete && this.props.onVideoComplete(this.props.video, eventData, VideoPlayerType.CVP);
    };

    @action handleContentEntryLoad = (playerID, contentID, isQueue) => {
        this.contentEntry = parseJSON(this.player.getContentEntry(contentID)) || {};
        const eventData = Object.assign({ playerID, contentID, isQueue }, this.contentEntry);
        VideoTracker.track(this.props.video, AnalyticsEventType.VIDEO_CONTENT_ENTRY_LOAD, eventData, VideoPlayerType.CVP);
        this.props.onContentEntryLoad && this.props.onContentEntryLoad(this.props.video, eventData, VideoPlayerType.CVP);
    };

    handleTrackingPaused = (playerID, dataObj) => {
        const eventData = Object.assign({}, dataObj, this.contentEntry);
        VideoTracker.track(this.props.video, AnalyticsEventType.VIDEO_PAUSE, eventData, VideoPlayerType.CVP);
        this.props.onVideoPause && this.props.onVideoPause(this.props.video, eventData, VideoPlayerType.CVP);
    };

    @action handleTrackingContentProgress = (playerID, dataObj) => {
        const eventData = Object.assign({}, dataObj, this.contentEntry);
        VideoTracker.track(this.props.video, AnalyticsEventType.VIDEO_PROGRESS, eventData, VideoPlayerType.CVP);
        this.props.onVideoProgress && this.props.onVideoProgress(this.props.video, eventData, VideoPlayerType.CVP);
        if (!this.hasTrackedFifty && dataObj.percent && dataObj.percent > 50) {
            this.hasTrackedFifty = true;
            VideoTracker.track(this.props.video, AnalyticsEventType.VIDEO_FIFTY, eventData, VideoPlayerType.CVP);
            this.props.onVideoFifty && this.props.onVideoFifty(this.props.video, eventData, VideoPlayerType.CVP);
        }
    };

    @action handleTrackingContentPlay = (playerID, dataObj) => {
        const eventData = Object.assign({}, dataObj, this.contentEntry);
        this.hasTrackedFifty = false;
        VideoTracker.track(this.props.video, AnalyticsEventType.VIDEO_START, eventData, VideoPlayerType.CVP);
        this.props.onVideoStart && this.props.onVideoStart(this.props.video, eventData, VideoPlayerType.CVP);
    };

}


export default CVPComponent;



// WEBPACK FOOTER //
// ./src/common/components/video-player/cvp.js